import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const CartContext = createContext(null);
const KEY = "eggman_cart_v1";

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitial);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1) => {
    const minQty = Number(product?.min_order_qty) || 0;
    const startQty = Math.max(qty, minQty);
    setItems((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        const newQty = Math.max(existing.qty + qty, minQty);
        return prev.map((p) => (p.id === product.id ? { ...p, qty: newQty, min_order_qty: minQty || null } : p));
      }
      return [...prev, { ...product, qty: startQty, min_order_qty: minQty || null }];
    });
  };

  const remove = (id) => setItems((prev) => prev.filter((p) => p.id !== id));

  const setQty = (id, qty) =>
    setItems((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        const minQty = Number(p.min_order_qty) || 1;
        return { ...p, qty: Math.max(minQty, qty) };
      })
    );

  const clear = () => setItems([]);

  const subtotal = items.reduce((acc, it) => acc + it.price * it.qty, 0);
  const count = items.reduce((acc, it) => acc + it.qty, 0);

  return (
    <CartContext.Provider value={{ items, add, remove, setQty, clear, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
